/**
 * GestorModulesContext — per-gestor module flags + realtime updates.
 *
 * Fetches /api/gestor/me/modules once on mount and exposes:
 *   - modules: { remesas: bool, conversor: bool, ... }
 *   - alwaysOn: string[]
 *   - registry: [{ key, label, icon, always_on, description, routes }]
 *   - isEnabled(key)
 *   - isPreview, previewAdminName
 *
 * Subscribes to the `gestor.modules.changed` WebSocket event so the admin's
 * PATCH instantly reflows the sidebar and (if applicable) redirects away
 * from a now-disabled section.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useRealtime } from './RealtimeContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Ctx = createContext(null);

const FALLBACK = {
  modules: {},
  alwaysOn: ['wallet', 'mis_pasajeros'],
  registry: [],
  isEnabled: (key) => key === 'wallet' || key === 'mis_pasajeros',
  isPreview: false,
  previewAdminName: null,
  loading: true,
  ready: false,
};

export const GestorModulesProvider = ({ children }) => {
  const { user, getAuthHeaders } = useAuth();
  const realtime = useRealtime();
  const [modules, setModules] = useState({});
  const [alwaysOn, setAlwaysOn] = useState(['wallet', 'mis_pasajeros']);
  const [registry, setRegistry] = useState([]);
  const [isPreview, setIsPreview] = useState(false);
  const [previewAdminName, setPreviewAdminName] = useState(null);
  const [previewMode, setPreviewMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const lastWarnRef = useRef(0);

  const fetchModules = useCallback(async () => {
    if (!user || user.role !== 'gestor') {
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API_URL}/gestor/me/modules`, { headers: getAuthHeaders() });
      setModules(r.data.enabled_modules || {});
      setAlwaysOn(r.data.always_on || ['wallet', 'mis_pasajeros']);
      setRegistry(r.data.registry || []);
      setIsPreview(!!r.data.preview);
      setPreviewAdminName(r.data.preview_admin_name || null);
      setPreviewMode(r.data.preview_mode || null);
      setReady(true);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[GestorModules] fetch failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  // Realtime: refetch when admin patches our flags. Toast only once per second.
  useEffect(() => {
    if (!realtime?.subscribe) return undefined;
    const off = realtime.subscribe('gestor.modules.changed', (data) => {
      if (!user || data?.gestor_id !== user.id) return;
      const now = Date.now();
      if (now - lastWarnRef.current > 1000) {
        toast.info('Tu administrador actualizó los módulos disponibles.', { duration: 3500 });
        lastWarnRef.current = now;
      }
      fetchModules();
    });
    return off;
  }, [realtime, user, fetchModules]);

  const isEnabled = useCallback((key) => {
    if (!key) return false;
    if (alwaysOn.includes(key)) return true;
    if (key in modules) return !!modules[key];
    // Unknown / not in registry → treated as enabled to avoid hiding legacy menus.
    return true;
  }, [modules, alwaysOn]);

  const value = useMemo(() => ({
    modules, alwaysOn, registry,
    isEnabled, isPreview, previewAdminName, previewMode,
    loading, ready,
    refresh: fetchModules,
  }), [modules, alwaysOn, registry, isEnabled, isPreview, previewAdminName, previewMode, loading, ready, fetchModules]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useGestorModules = () => useContext(Ctx) || FALLBACK;
